import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  Trophy,
  ListOrdered,
  Hash,
  Users,
  PlayCircle,
  Award,
  AlertTriangle,
  Settings,
  ArrowLeft,
  LogOut,
  Layers,
} from "lucide-react";
import { LOGO_MARK, TAGLINE } from "../lib/brand";
import { LogoMark } from "./Logo";

const ADMIN_NAV = [
  { to: "/admin", label: "Übersicht", icon: LayoutDashboard, end: true, testid: "admin-nav-overview" },
  { to: "/admin/tournaments", label: "Turniere", icon: Trophy, testid: "admin-nav-tournaments" },
  { to: "/admin/live", label: "Live Matches", icon: PlayCircle, testid: "admin-nav-live" },
  { to: "/admin/boards", label: "Boards (Kanban)", icon: Layers, testid: "admin-nav-boards" },
  { to: "/admin/leaderboard", label: "Rangliste (klassisch)", icon: ListOrdered, testid: "admin-nav-leaderboard" },
  { to: "/admin/channels", label: "Chat Kanäle", icon: Hash, testid: "admin-nav-channels" },
  { to: "/admin/achievements", label: "Erfolge & Pokale", icon: Award, testid: "admin-nav-achievements" },
  { to: "/admin/tickets", label: "Tickets & Reports", icon: AlertTriangle, testid: "admin-nav-tickets" },
  { to: "/admin/users", label: "Mitglieder", icon: Users, testid: "admin-nav-users" },
  { to: "/admin/settings", label: "Settings & NWR ID", icon: Settings, testid: "admin-nav-settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-72 shrink-0 bg-surface/50 backdrop-blur-xl border-r border-white/10 flex flex-col">
        <div className="p-6 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-2.5" data-testid="admin-logo">
            <LogoMark src={LOGO_MARK} size="w-7 h-7" />
            <div>
              <div className="brand-text text-lg leading-none">NORDGAMING</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mt-1">
                Admin Console · {TAGLINE}
              </div>
            </div>
          </Link>
        </div>

        <nav className="p-4 flex-1 overflow-y-auto space-y-1">
          {ADMIN_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors border-l-2 ${
                  isActive
                    ? "bg-neon-gradient-soft text-white border-primary"
                    : "text-white/70 hover:text-white hover:bg-white/5 border-transparent"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={1.5} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-2">
          <Link to="/dashboard" className="btn-ghost w-full justify-start" data-testid="exit-admin">
            <ArrowLeft className="w-4 h-4" /> Zurück zur App
          </Link>
          <div className="flex items-center gap-2 px-2 py-1.5">
            <div className="w-8 h-8 bg-neon-gradient-soft border border-primary/30 text-primary flex items-center justify-center font-mono text-xs rounded-sm">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-magenta">ADMIN</div>
            </div>
            <button onClick={handleLogout} className="btn-ghost !p-2" data-testid="admin-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <div className="max-w-6xl mx-auto p-6 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
